import { Button, Form, Input, Space } from "antd";
import { PaymentTextMessage } from "utils/paymentTextMessage";
import { PlusOutlined } from "@ant-design/icons";
import { useAuth } from "auth/useAuth";
import { useCallback } from "react";
import { useFormTextInserter } from "utils/useTextInserter";
import type { NamePath } from "antd/es/form/interface";
import type { Rule } from "antd/es/form";

/**
 * Maximum number of characters allowed by Opera messaging.
 *
 * Keep this synchronized with `OperaClient.SMSMaxLength` in the back-end.
 */
const errorLimit = 300;

/** Return the name of the current Provider. */
function useProviderName() {
    const loginResult = useAuth((state) => state.loginResult);
    return loginResult?.customerName;
}

/**
 * Expand a template's tokens to use the maximum expected amount of space.
 * @param tpl The template to expand.
 * @param providerName Optional real provider name for maximum fidelity.
 * @returns The expanded template.
 */
function expandTemplate(tpl: string | undefined, providerName?: string) {
    const template = new PaymentTextMessage(tpl ?? "");
    return template.getMaximumTextMessage(providerName);
}

/** Generate validation rules, taking into account the current Provider's name. */
function useRules(disabled: boolean) {
    const providerName = useProviderName();

    return disabled
        ? []
        : ([
              {
                  type: "string",
                  required: true,
                  whitespace: true,
                  message: "Custom message must be provided when enabled.",
              },
              {
                  pattern: /\[\[Payment Link\]\]/i,
                  message: "Message must include payment link placeholder for patients to make payments.",
              },
              {
                  max: errorLimit,
                  transform: (tpl: string | undefined) => expandTemplate(tpl, providerName),
                  message: `Messages may not exceed ${errorLimit} characters.`,
              },
          ] satisfies Rule[]);
}

function useCountStrategy() {
    const providerName = useProviderName();
    return useCallback((value: string) => expandTemplate(value, providerName).length, [providerName]);
}

interface TokenButtonsProps {
    readonly onUpdate?: () => void;
    readonly disabled?: boolean;
    readonly inserter: (toInsert: string) => Promise<void>;
}

const TokenButtons = ({ onUpdate, disabled, inserter }: TokenButtonsProps) => {
    const buttonProps = {
        "aria-disabled": disabled,
        disabled,
        icon: <PlusOutlined />,
        size: "small",
        type: "link",
    } as const;

    const insert = (token: string) => void inserter(token).then(() => onUpdate?.());

    return (
        <Space direction="horizontal">
            <Button {...buttonProps} onClick={() => insert("[[First Name]]")}>
                First Name
            </Button>
            <Button {...buttonProps} onClick={() => insert("[[Amount Due]]")}>
                Amount Due
            </Button>
            <Button {...buttonProps} onClick={() => insert("[[Payment Link]]")}>
                Payment Link
            </Button>
        </Space>
    );
};

interface CustomMessageProps<TForm> {
    readonly formName: string;
    readonly fieldName: NamePath<TForm>;
    readonly onUpdate?: () => void;
    readonly disabled: boolean;
}

/** Form field used to input a custom SMS message with insertable replacement tokens. */
export const CustomMessage = <TForm,>({ formName, fieldName, onUpdate, disabled }: CustomMessageProps<TForm>) => {
    const form = Form.useFormInstance<TForm>();
    const [inserter, inserterBusy] = useFormTextInserter(form, formName, fieldName);

    const rules = useRules(disabled);
    const strategy = useCountStrategy();

    // NOTE: We make the raw input max length longer than the provided limit because template tokens may actually be
    // longer than the text we're substituting in (e.g. "[[Amount Due]]" is 14 characters, but the actual amount due
    // string is less than that).
    return (
        <Space direction="vertical">
            <TokenButtons onUpdate={onUpdate} disabled={disabled || inserterBusy} inserter={inserter} />
            <Form.Item name={fieldName} required={!disabled} rules={rules}>
                <Input.TextArea
                    aria-label="Custom message text"
                    className="vyne-input-textarea"
                    readOnly={inserterBusy}
                    disabled={disabled}
                    aria-disabled={disabled}
                    maxLength={errorLimit * 2}
                    rows={4}
                    count={{ max: errorLimit, strategy, show: true }}
                />
            </Form.Item>
        </Space>
    );
};
